import React, { useEffect, useState } from 'react';
import { ArrowRight, Play, CheckCircle, X } from 'lucide-react';

const HeroSection = () => {
  const [isDemoOpen, setIsDemoOpen] = useState(false);

  useEffect(() => {
    if (!isDemoOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setIsDemoOpen(false); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [isDemoOpen]);

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative w-full min-h-screen overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent z-10"></div>
        <picture>
          <source
            media="(max-width: 640px)"
            srcSet="/images/hero-london-skyline-mobile-540.webp 540w, /images/hero-london-skyline-mobile-720.webp 720w"
            sizes="100vw"
          />
          <img
            src="/images/hero-london-skyline-1600.webp"
            srcSet="/images/hero-london-skyline-800.webp 800w, /images/hero-london-skyline-1200.webp 1200w, /images/hero-london-skyline-1600.webp 1600w, /images/hero-london-skyline-1920.webp 1920w"
            sizes="100vw"
            width="1600"
            height="1067"
            alt="UK commercial and heritage buildings served by Digital Scanning Group 3D laser scanning services"
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover"
          />
        </picture>
      </div>

      {/* Animated Background Elements */}
      <div className="absolute inset-0 z-20">
        <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
      </div>

      {/* Hero Content */}
      <div className="relative z-30 h-full flex items-center pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <div className="max-w-4xl">
            {/* Badge */}
            <div
              className={`inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full mb-8`}
            >
              <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
              <span className="text-white text-sm font-medium">UK's Leading 3D Scanning & Digital Twin Specialists</span>
            </div>

            {/* Main Heading */}
            <h1
              className={`text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-6 leading-tight`}
            >
              Matterport 3D Scanning{' '}
              <span className="bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 bg-clip-text text-transparent">
                UK
              </span>
            </h1>

            {/* Tagline */}
            <p
              className={`text-2xl md:text-3xl text-cyan-300 font-semibold mb-4`}
            >
              The UK's Trusted 3D Scanning & Digital Twin Specialists
            </p>

            {/* Subheading */}
            <p
              className={`text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed`}
            >
              Matterport progress capture, point cloud surveys and gridline checks for construction projects, and 3D tours of vacant units, offices, hotels and retail for commercial real estate. Fixed prices, delivered within 48 hours, London-based and working across the UK.
            </p>

            {/* Trust Indicators */}
            <div
              className={`flex flex-wrap items-center gap-x-6 gap-y-3 mb-10`}
            >
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-cyan-400" />
                <span className="text-white/90 text-sm font-medium">Trusted by NHS Trusts</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-cyan-400" />
                <span className="text-white/90 text-sm font-medium">Tier 1 Contractors</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-cyan-400" />
                <span className="text-white/90 text-sm font-medium">FTSE 100 Companies</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-cyan-400" />
                <span className="text-white/90 text-sm font-medium">1,000+ Scans Completed</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div
              className={`flex flex-wrap gap-6`}
            >
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToContact();
                }}
                className="group px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-full hover:shadow-2xl hover:shadow-cyan-500/50 transition-all duration-300 hover:scale-105 flex items-center gap-2"
                aria-label="Get a free quote for 3D scanning services"
              >
                Get a Free Quote
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <button
                onClick={() => setIsDemoOpen(true)}
                className="group px-8 py-4 bg-white/10 backdrop-blur-md border-2 border-white/30 text-white font-semibold rounded-full hover:bg-white/20 transition-all duration-300 hover:scale-105 flex items-center gap-2"
                aria-label="Watch a demo of our 3D scanning technology"
              >
                <Play className="h-5 w-5" />
                Watch Demo
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-30">
        <div className="flex flex-col items-center gap-2 animate-bounce">
          <span className="text-white/60 text-sm">Scroll to explore</span>
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-white/60 rounded-full"></div>
          </div>
        </div>
      </div>

      {isDemoOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
          onClick={() => setIsDemoOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="DSG demo video"
        >
          <button
            onClick={() => setIsDemoOpen(false)}
            className="absolute top-5 right-5 text-white/80 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20 transition"
            aria-label="Close demo"
          >
            <X className="h-6 w-6" />
          </button>
          <div
            className="relative w-full max-w-5xl aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <video
              src="/dsg-demo.mp4"
              controls
              autoPlay
              playsInline
              className="w-full h-full"
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default HeroSection;
